// The list API may still return legacy rows alongside video reservation metadata.
function numberValue(value) {
  if (typeof value !== 'number' && typeof value !== 'string') return null
  if (typeof value === 'string' && !value.trim()) return null
  const number = Number(value)
  if (!Number.isFinite(number)) return null
  return number === 0 ? 0 : number
}

function extraInfo(row) {
  const extra = row && row.extra_info
  if (typeof extra === 'string') {
    try {
      const parsed = JSON.parse(extra)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
    } catch (_) {
      return {}
    }
  }
  return extra && typeof extra === 'object' && !Array.isArray(extra) ? extra : {}
}

function sourceType(row) {
  return (row && row.source_type) || extraInfo(row).source_type
}

export function hasPointsRecordDetails(row) {
  return Boolean(row && Array.isArray(row.details) && row.details.length)
}

export function isVideoPointsRecord(row) {
  return ['video_usage', 'video_reservation'].includes(sourceType(row))
}

export function signedPointsChange(row) {
  // A group may inherit source_type from its latest informational record.
  if (hasPointsRecordDetails(row)) {
    const signed = numberValue(row.points_change_signed)
    return signed === null
      ? row.details.reduce((sum, detail) => sum + signedPointsChange(detail), 0)
      : signed
  }
  const source = sourceType(row)
  if (source === 'video_reservation') return 0

  const number = numberValue(row && row.points_change) || 0
  // Ordinary legacy debits can carry a contradictory gain type or signed value.
  if (source !== 'video_usage' && number < 0) return number

  const signed = numberValue(row && row.points_change_signed)
  if (signed !== null) return signed
  if (source === 'video_usage') {
    const delta = numberValue(extraInfo(row).points_delta)
    // Backend deltas are costs: a positive delta reduces the balance.
    if (delta !== null) return delta === 0 ? 0 : -delta
  }
  if (number === 0) return 0
  if (Number(row && row.operation_type) === 2) return -Math.abs(number)
  return number
}

export function pointsChangeClass(row) {
  const change = signedPointsChange(row)
  return change > 0 ? 'is-positive' : change < 0 ? 'is-negative' : 'is-neutral'
}

export function heldPoints(row) {
  if (hasPointsRecordDetails(row)) {
    const total = numberValue(row.held_points)
    return total === null
      ? row.details.reduce((sum, detail) => sum + heldPoints(detail), 0)
      : Math.max(0, total)
  }
  if (sourceType(row) !== 'video_reservation') return 0
  const extra = extraInfo(row)
  // Terminal reservation rows must never show the original hold as outstanding.
  if (extra.reservation_status !== 'reserved') return 0
  const held = numberValue(extra.held_points)
  return Math.max(0, held === null ? numberValue(extra.reserved_points) || 0 : held)
}

export function videoPointsStatus(row) {
  if (hasPointsRecordDetails(row)) {
    if (heldPoints(row) > 0) return 'group_held'
    const financial = row.details.filter(detail => sourceType(detail) === 'video_usage')
    const candidates = financial.length ? financial : row.details.filter(detail =>
      sourceType(detail) === 'video_reservation' &&
      ['committed', 'released'].includes(extraInfo(detail).reservation_status)
    )
    // Informational rows can be newer than the charge; use the latest financial event.
    const timestamp = detail => Date.parse(String(detail.create_time || '').replace(' ', 'T')) || 0
    const latest = candidates.reduce((current, detail) =>
      !current || timestamp(detail) > timestamp(current) ? detail : current, null)
    return latest ? videoPointsStatus(latest) : ''
  }
  const extra = extraInfo(row)
  if (sourceType(row) === 'video_reservation') {
    return ['reserved', 'committed', 'released'].includes(extra.reservation_status)
      ? extra.reservation_status
      : ''
  }
  if (sourceType(row) !== 'video_usage') return ''
  if (extra.operation_kind === 'manual') return 'adjustment'
  if (signedPointsChange(row) > 0) return 'refund'
  if (extra.operation_kind === 'delta') {
    return signedPointsChange(row) < 0 ? 'supplemental' : 'settled'
  }
  if (extra.operation_kind === 'provisional' || [true, 1, '1', 'true'].includes(extra.provisional)) {
    return 'provisional'
  }
  return 'settled'
}

export function pointsRecordKey(row, index = 0, page = 1) {
  if (row && row.group_key) return String(row.group_key)
  if (row && row.run_id) return 'run:' + row.run_id
  return pointsDetailKey(row, index, 'record:' + page)
}

export function pointsDetailKey(row, index = 0, prefix = 'detail') {
  if (row && row.group_key) return String(row.group_key)
  if (row && (row.record_uuid || row.id)) return String(row.record_uuid || row.id)
  const extra = extraInfo(row)
  if (sourceType(row) === 'video_reservation' && extra.reservation_id) {
    return 'reservation:' + extra.reservation_id
  }
  return prefix + ':' + index
}

// Stable child identities preserve expansion when an intent group becomes a Run group.
export function pointsRecordIdentities(row) {
  if (!row || typeof row !== 'object') return []
  const extra = extraInfo(row)
  const identities = []
  for (const [kind, value] of Object.entries({
    group: row.group_key,
    run: row.run_id,
    record: row.record_uuid || row.id,
    intent: extra.execution_intent_id || row.execution_intent_id,
    reservation: extra.reservation_id
  })) {
    if (value) identities.push(kind + ':' + value)
  }
  if (hasPointsRecordDetails(row)) identities.push(...row.details.flatMap(pointsRecordIdentities))
  return identities
}
